import { useDispatch } from "react-redux";

import { ViewList } from "@material-ui/icons";
import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import { IS_AWS_ENV } from "constants/app.constants";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { useAtomValue } from "jotai";
import { updateChartByPath } from "store/features";
import styled from "styled-components/macro";

import { useChartEntities } from "hooks/charts";

import { ToolbarButton } from "components/base";
import {
  FullscreenToggle,
  LegendToggle,
  ScreenshotToggle
} from "components/charts/toggles";
import ChartFocusToggle from "components/charts/toggles/focus/ChartFocusToggle";

import { visibilityToggleStyles } from "../../shared/SharedStyles";
import DrainLegsToggle from "./DrainLegsToggle";
import WellStatusChartSelector from "./WellStatusChartSelector";

/**
 * Minimal toolbar for the Well Status widget.
 *
 * NOT the shared ChartToolbar: its Well component array hard-renders the wells
 * ChartSelector + ProductSelector header and toggles that don't read feature
 * `show` flags, so it would surface wells-production controls on this chart.
 *
 * ChartFocusToggle is the EXISTING wells chart-focus feature, reused rather than
 * rebuilt (research D9) — it gives the ticket's "Chart focus (add that option in
 * chart)" including the group-by picker, bin settings and palette, and self-hides
 * unless the chart type enables `features.chartFocus.show`.
 */
function WellStatusChartToolbar() {
  const dispatch = useDispatch();
  const { id } = useChartsContext();
  const { screenshot, dataTable, chartType } = useChartEntities(id);
  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;
  const isScreenshotActive = screenshot?.active ?? false;
  const isDataTableActive = dataTable?.active ?? false;
  const isDataTableEnabled = dataTable?.enabled ?? false;
  // Drain-legs toggle is over-time only — Current Well Status keeps the flag
  // pinned and never evaluates leg-ness (see wellStatusCurrent.ts / EVA-120
  // plan §4.4). Same isOverTime convention as WellStatusChartWrapper.
  const isOverTime = chartType === EvaChart.WellStatusOverTime;

  const toggleDataTable = (visible: boolean) => {
    dispatch(
      updateChartByPath({ id, path: `features.datatable.active`, value: visible })
    );
  };

  // Fully hidden during capture (not just emptied) — an empty bar shows up as a
  // white band at the top of the screenshot.
  if (isScreenshotActive) return null;

  return (
    <Wrapper
      className={classnames({ visible: isHover })}
      data-testid="well-status-chart-toolbar">
      <HeaderGroup>
        {/* Raw data table (export lives inside it, not on the toolbar) — same
            placement as the rig/frac header: left of the chart selector. */}
        {isDataTableEnabled && (
          <ToolbarButton
            style={{ marginLeft: "4px", marginRight: "4px" }}
            active={isDataTableActive}
            icon={<ViewList fontSize="large" />}
            tooltipText="Data Table"
            overflowLabel="Data Table"
            onToggle={toggleDataTable}
            data-testid="well-status-data-table-toggle"
          />
        )}
        <WellStatusChartSelector />
      </HeaderGroup>
      <ToolbarContent>
        {/* No status or time-scale button here: both charts mount their status
            picker on the axis it titles, and the over-time chart its time scale
            on the x-axis (see WellStatusChartWrapper), per the mockup. */}
        <ChartFocusToggle />
        {/* AWS statuses never carry a DRAIN form, so the flag cannot change a result. */}
        {isOverTime && !IS_AWS_ENV && <DrainLegsToggle />}
        <LegendToggle />
        <ScreenshotToggle />
        <FullscreenToggle />
      </ToolbarContent>
    </Wrapper>
  );
}

export default WellStatusChartToolbar;

const Wrapper = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--chart-toolbar-height);
  display: flex;
  align-items: center;
  background-color: var(--white);
  box-shadow: 0 14px 16px 0 rgb(45 53 63 / 10%), inset 0 7px 8px -10px rgb(45 53 63 / 15%);
  z-index: 50;
  padding-right: 4px;
`;

const HeaderGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  flex: 1;
`;
